import {t} from "i18next";
import {all, call, delay, put, select, takeLatest} from "redux-saga/effects";
import {setIncidentAssigneesSuccess} from "store/features/assigneesSlice";
import {
  getIncident,
  getIncidentAuditLog,
  getIncidentAuditLogFailure,
  getIncidentAuditLogSuccess,
  getIncidentFailure,
  getIncidentNotes,
  getIncidentNotesFailure,
  getIncidentNotesSuccess,
  getIncidentSuccess,
  patchIncident,
  patchIncidentFailure,
  patchIncidentSuccess,
  postIncidentNote,
  postIncidentNoteFailure,
  postIncidentNoteSuccess
} from "store/features/incidentDetailSlice";
import {
  getEvents,
  getEventsFailure,
  getEventsSuccess
} from "store/features/incidentDetectionSlice";
import {
  getEntitiesByTypeFailure,
  getEntitiesByTypeSuccess,
  getIncidentSummary,
  getIncidentSummaryFailure,
  getIncidentSummarySuccess
} from "store/features/incidentOverviewSlice";
import {
  getIncidentPlaybookFailure,
  getIncidentPlaybookSuccess,
  postIncidentWorkflow,
  postIncidentWorkflowFailure,
  postIncidentWorkflowSuccess
} from "store/features/incidentResponseSlice";
import {
  setErrorNotification,
  setSuccessNotification
} from "store/features/notificationsSlice";
import {
  selectAssetsData,
  selectObservablesData
} from "store/selectors/incidentOverviewSelectors";
import {
  fetchEventsForIncident,
  fetchIncidentAuditLog,
  fetchIncidentById,
  fetchIncidentNotes,
  fetchIncidentPlaybook,
  fetchIncidentSummary,
  postExecutionWorkflow,
  postIncidentNote as postIncidentNoteApi,
  updateIncident
} from "api/incidentDetail";
import {FieldNames} from "models/fields";
import {makeEntityObjectWithArraysByKey} from "utils/arrayUtils";
import {ENTITY_TYPE} from "utils/entities";
import {formatGraphData} from "utils/graph/graphFormatting";
import {prettyPrintUsernamesFromArray} from "utils/users";
import {getShortId} from "../../utils/misc";
import {
  selectAllUsers,
  selectIncidentDetailId,
  selectIncidentDetailShortId,
  selectResponseActionsExecuted
} from "../selectors";

export function* getIncidentSaga(action) {
  try {
    const {id} = action.payload;
    const [incident] = yield call(fetchIncidentById, id);

    if (incident) {
      yield put(
        getIncidentSuccess({
          id: incident.id,
          incident: incident
        })
      );
      yield put(getIncidentSummary({id}));
    } else {
      // Incident Id Not Found
      yield put(getIncidentFailure());
    }
  } catch (error) {
    console.error(error);
    yield put(getIncidentFailure(error));
    yield put(
      setErrorNotification(t("incidentDetail.messages.detailLoadingError"))
    );
  }
}

export function* getIncidentAuditLogSaga(action) {
  try {
    const {
      shortId: shortIdProp,
      sortBy,
      currentPage,
      resultsPerPage
    } = action.payload;

    const pathNameArray = window.location.pathname.split("/");
    const shortId = shortIdProp
      ? shortIdProp
      : getShortId(pathNameArray[pathNameArray.length - 2]);

    const auditLog = yield call(fetchIncidentAuditLog, {
      shortId,
      sortBy,
      currentPage,
      resultsPerPage
    });

    yield put(getIncidentAuditLogSuccess(auditLog));
  } catch (error) {
    console.error(error);
    yield put(getIncidentAuditLogFailure(error));
    yield put(
      setErrorNotification(t("incidentDetail.messages.detailLoadingError"))
    );
  }
}

export function* refetchAuditLogAfterPatchSaga() {
  try {
    const shortId = yield select(selectIncidentDetailShortId);

    // Wait until patch updates the database
    yield delay(1000);
    const auditLog = yield call(fetchIncidentAuditLog, {shortId});

    yield put(getIncidentAuditLogSuccess(auditLog));
  } catch (error) {
    yield put(getIncidentAuditLogFailure(error));
    yield put(
      setErrorNotification(t("incidentDetail.messages.detailLoadingError"))
    );
  }
}

export function* getIncidentPlaybookSaga() {
  try {
    const response = yield call(fetchIncidentPlaybook);
    yield put(getIncidentPlaybookSuccess(response));
  } catch (error) {
    console.error(error);
    yield put(getIncidentPlaybookFailure(error));
    yield put(setErrorNotification(t("incidentDetail.messages.playbookError")));
  }
}

export function* postIncidentWorkflowSaga(action) {
  try {
    const actionsExecuted = yield select(selectResponseActionsExecuted);
    const incidentId = yield select(selectIncidentDetailId);
    const {coaId, workflowId, values} = action.payload;

    const payload = {
      incidentId,
      ...action.payload
    };

    yield call(postExecutionWorkflow, payload);

    // Create map with COA ID and executed items to use for status polling
    const actionState = actionsExecuted?.[coaId] || [];
    const responseActionsExecuted = {
      ...actionsExecuted,
      [coaId]: [
        ...actionState,
        {
          status: "started",
          values: values?.map((entity) => entity?.id) || [],
          workflowId
        }
      ]
    };

    yield put(postIncidentWorkflowSuccess(responseActionsExecuted));
    yield put(setSuccessNotification(t("response.messages.workflowSuccess")));
  } catch (error) {
    console.error(error);
    yield put(postIncidentWorkflowFailure(error));
    yield put(setErrorNotification(t("response.messages.workflowFailure")));
  }
}

export function* getIncidentNotesSaga(action) {
  try {
    const {shortId, sortBy, currentPage, resultsPerPage} = action.payload;
    const {dataRows, totalRows} = yield call(fetchIncidentNotes, {
      shortId,
      sortBy,
      currentPage,
      resultsPerPage
    });

    const usersList = yield select(selectAllUsers);
    const flatNotesArray = dataRows?.data.map(
      (note) => note?.data?.additional?.note
    );
    const dataRowsWithUserName = prettyPrintUsernamesFromArray(
      flatNotesArray,
      usersList
    );

    yield put(
      getIncidentNotesSuccess({
        dataRows: dataRowsWithUserName,
        totalRows
      })
    );
  } catch (error) {
    console.error(error);
    yield put(getIncidentNotesFailure(error));
    yield put(
      setErrorNotification(t("incidentDetail.worklogTab.notesPanel.fetchError"))
    );
  }
}

export function* postIncidentNoteSaga(action) {
  try {
    const {content, additionalEntities, sortBy, ...restPayload} =
      action.payload;
    const incidentId = yield select(selectIncidentDetailId);

    const note = {
      content,
      incidentId,
      additionalEntities
    };

    const newNote = yield call(postIncidentNoteApi, note);
    const usersList = yield select(selectAllUsers);
    const newNoteWithUserName = prettyPrintUsernamesFromArray(
      [newNote],
      usersList
    );

    yield put(
      postIncidentNoteSuccess({
        sortBy,
        ...restPayload,
        ...newNoteWithUserName[0] // prettyPrintUsernamesFromArray returns an array. This case will return one time in array.
      })
    );
    yield put(
      setSuccessNotification(
        t("incidentDetail.worklogTab.notesPanel.postSuccess")
      )
    );
  } catch (error) {
    console.error(error);
    yield put(postIncidentNoteFailure(error));
    yield put(
      setErrorNotification(t("incidentDetail.worklogTab.notesPanel.postError"))
    );
  }
}

export function* patchIncidentSaga(action) {
  try {
    const {incidentId, patch} = action.payload;
    yield call(updateIncident, incidentId, patch);
    yield put(patchIncidentSuccess({incidentId, patch}));
    yield put(
      setSuccessNotification(t("incidentDetail.messages.patchSuccess"))
    );
  } catch (error) {
    console.error(error);
    yield put(patchIncidentFailure(error));
    yield put(setErrorNotification(t("incidentDetail.messages.patchError")));
  }
}

export function* getIncidentOverviewSaga(action) {
  try {
    const {id} = action.payload;
    const resp = yield call(fetchIncidentSummary, id);
    delete Object.assign(resp, {["assets"]: resp.targets}).targets; // @todo Remap `targets` to `assets`. Ideally endppint would provide mapping so this isn't needed.
    const formattedAttackGraphData = yield call(formatGraphData, resp.graph);
    yield put(
      getIncidentSummarySuccess({...resp, ...formattedAttackGraphData})
    );
  } catch (error) {
    console.error(error);
    yield put(getIncidentSummaryFailure(error));
    yield put(
      setErrorNotification(t("overview.messages.overviewLoadingError"))
    );
  }
}

export function* getEntitiesByTypeSaga() {
  try {
    const assets = yield select(selectAssetsData);
    const observables = yield select(selectObservablesData);

    const assetsByType = makeEntityObjectWithArraysByKey(
      assets,
      FieldNames.observableType,
      ENTITY_TYPE.assets
    );

    const observablesByType = makeEntityObjectWithArraysByKey(
      observables,
      FieldNames.type,
      ENTITY_TYPE.observables
    );

    yield put(
      getEntitiesByTypeSuccess({
        [ENTITY_TYPE.assets]: assetsByType,
        [ENTITY_TYPE.observables]: observablesByType
      })
    );
  } catch (error) {
    console.error(error);
    yield put(getEntitiesByTypeFailure(error));
  }
}

export function* getEventsSaga(action) {
  try {
    const {id} = action.payload;
    const {events} = yield call(fetchEventsForIncident, id);
    yield put(getEventsSuccess(events));
  } catch (error) {
    console.error(error);
    yield put(getEventsFailure(error));
  }
}

export default function* incidentDetailSagas() {
  yield all([
    yield takeLatest(getIncident, getIncidentSaga),
    yield takeLatest(getIncidentAuditLog, getIncidentAuditLogSaga),
    yield takeLatest(
      [setIncidentAssigneesSuccess, patchIncidentSuccess],
      refetchAuditLogAfterPatchSaga
    ),
    yield takeLatest(getIncidentSuccess, getIncidentPlaybookSaga),
    yield takeLatest(getIncidentNotes, getIncidentNotesSaga),
    yield takeLatest(postIncidentNote, postIncidentNoteSaga),
    yield takeLatest(postIncidentWorkflow, postIncidentWorkflowSaga),
    yield takeLatest(patchIncident, patchIncidentSaga),
    yield takeLatest(getIncidentSummary, getIncidentOverviewSaga),
    yield takeLatest(getIncidentSummarySuccess, getEntitiesByTypeSaga),
    yield takeLatest(getEvents, getEventsSaga)
  ]);
}
